// =====================================================================
// My Agency V2 — avatar treatment (ARTBOARD-SPEC §2 "Avatar initial
// palettes" + CMP-CARD header anatomy).
//
// PersonAvatar: circle; photo when available, colored-initials fallback
//   (palette rotates deterministically per entity — the artboard's
//   per-person colors are ILLUSTRATIVE, the rotation rule is binding).
//   Sizes/initial font sizes per spec: 44px card → 15px/600,
//   34–38px list/sheet → 12–13px/600, 22px "Managed by" → 9.5px/600,
//   56px profile header → 18px/600.
// OrgAvatar: rounded square (partner orgs) — 64px r16 → 20px/700,
//   44px r12 → 15px/700, 36px r10 → 13px/700, 56px r14 (logo tile).
//
// Purely presentational: photo error state falls back to initials
// internally (same idiom as PlayersGrid's imageErrors, kept local).
// =====================================================================
import React, { useState } from "react";
import { AVATAR_PALETTES, AVATAR_PALETTE_PENDING } from "./tokens";

export const getInitials = (name, max = 2) => {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, max);
};

// Deterministic palette rotation per entity (stable across renders).
export const pickPalette = (seed) => {
  const s = String(seed ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 997;
  return AVATAR_PALETTES[h % AVATAR_PALETTES.length];
};

// Initial font size per avatar px size (spec mapping, person circles).
const personInitialFont = (size) => {
  if (size >= 56) return { fontSize: 18, fontWeight: 600 };
  if (size >= 44) return { fontSize: 15, fontWeight: 600 };
  if (size >= 34) return { fontSize: 12.5, fontWeight: 600 };
  return { fontSize: 9.5, fontWeight: 600 };
};

// Initial font size per org avatar px size (rounded squares).
const orgInitialFont = (size) => {
  if (size >= 64) return { fontSize: 20, fontWeight: 700 };
  if (size >= 44) return { fontSize: 15, fontWeight: 700 };
  return { fontSize: 13, fontWeight: 700 };
};

export const PersonAvatar = ({
  src,
  name,
  seed,
  size = 44,
  pending = false,
  style,
  ...rest
}) => {
  const [broken, setBroken] = useState(false);
  const palette = pending ? AVATAR_PALETTE_PENDING : pickPalette(seed ?? name);
  const base = {
    width: size,
    height: size,
    borderRadius: "50%",
    flex: "none",
    ...style,
  };
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || ""}
        style={{ ...base, objectFit: "cover" }}
        onError={() => setBroken(true)}
        {...rest}
      />
    );
  }
  return (
    <span
      style={{
        ...base,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: palette.bg,
        color: palette.text,
        ...personInitialFont(size),
      }}
      aria-label={name || undefined}
      {...rest}
    >
      {getInitials(name)}
    </span>
  );
};

export const OrgAvatar = ({
  src,
  name,
  seed,
  size = 64,
  radius = 16,
  pending = false,
  style,
  ...rest
}) => {
  const [broken, setBroken] = useState(false);
  const palette = pending ? AVATAR_PALETTE_PENDING : pickPalette(seed ?? name);
  const base = {
    width: size,
    height: size,
    borderRadius: radius,
    flex: "none",
    ...style,
  };
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name || ""}
        style={{ ...base, objectFit: "cover" }}
        onError={() => setBroken(true)}
        {...rest}
      />
    );
  }
  return (
    <span
      style={{
        ...base,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: palette.bg,
        color: palette.text,
        ...orgInitialFont(size),
      }}
      aria-label={name || undefined}
      {...rest}
    >
      {getInitials(name)}
    </span>
  );
};
